import React, { useContext, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import SubscriptionCard from '../../components/PricingCards/index.jsx';
import TopUpCard from '../../components/TopUpCard/TopUpCard';
import './Pricing.scss';

// Import ThemeContext
import { ThemeContext } from '../../ThemeContext';

// Import provider images
import groq from '../../assets/groqlogo.png';
import openai from '../../assets/chatgpt-logo.png';
import anthropic from '../../assets/claude-logo (1).png';
import google from '../../assets/google-ai.png';
import mindmade from '../../assets/Slika2.png';

// Import light theme images
import ChatGptWhite from '../../assets/chatgpt-logo-white.png';
import MindMadeWhite from '../../assets/slika1.png';
import { loadPricesApi } from '../../api/index.js';

const subscriptionPlans = [
  {
    planName: 'Indie',
    credits: 7100,
    flowNodeRuns: 5000,
    storage: '1,000 MB',
    maxCreditsToHold: 100000,
    price: '8.19',
    priceWithVat: '9.99',
  },
  {
    planName: 'Startup',
    credits: 21200,
    flowNodeRuns: 10000,
    storage: '5,000 MB',
    maxCreditsToHold: 200000,
    price: '24.58',
    priceWithVat: '29.99',
    isPopular: true,
  },
  {
    planName: 'Growth',
    credits: 55600,
    flowNodeRuns: 20000,
    storage: '25,000 MB',
    maxCreditsToHold: 500000,
    price: '65.57',
    priceWithVat: '80.00',
  },
  {
    planName: 'Scale',
    credits: 140000,
    flowNodeRuns: 50000,
    storage: '50,000 MB',
    maxCreditsToHold: 1000000,
    price: '163.93',
    priceWithVat: '199.99',
  },
];

const topUpPlans = [
  {
    planName: 'Essential',
    creditBalance: 3200,
    price: '4.09',
    priceWithVat: '4.99',
  },
  {
    planName: 'Standard',
    creditBalance: 6400,
    price: '8.19',
    priceWithVat: '9.99',
  },
  {
    planName: 'Advanced',
    creditBalance: 15900,
    price: '20.48',
    priceWithVat: '24.99',
  },
  {
    planName: 'Professional',
    creditBalance: 31900,
    price: '40.98',
    priceWithVat: '50.00',
  },
  {
    planName: 'Premium',
    creditBalance: 63800,
    price: '81.96',
    priceWithVat: '99.99',
  },
];

const animations = {
  container: {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
  },
  item: {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.6, ease: 'easeOut' },
    },
  },
};

const Pricing = () => {
  const { theme } = useContext(ThemeContext);
  const [data, setData] = useState([]);

  console.log('Pricing page');

  useEffect(() => {
    console.log('Fetching prices data from API...');
    Promise.resolve()
      .then(() => loadPricesApi())
      .then((data) => {
        console.log('Loaded prices data:', data);

        setData(
          data.filter(
            (m) =>
              m.price_per_million_prompt_tokens &&
              m.price_per_million_completion_tokens
          )
        );
      })
      .catch((error) => {
        console.error('Error loading prices:', error);
      });
  }, []);

  // SIMPLE IF STATEMENT FOR IMAGE SELECTION - same logic as navbar
  const getProviderImage = (provider) => {
    if (provider === 'openai') {
      // Light theme = dark logo, Dark theme = white logo
      if (theme === 'light') {
        return openai; // Dark logo for light background
      } else {
        return ChatGptWhite; // White logo for dark background
      }
    }

    if (provider === 'mindmade') {
      // Light theme = dark logo, Dark theme = white logo
      if (theme === 'light') {
        return mindmade; // Dark logo for light background
      } else {
        return MindMadeWhite; // White logo for dark background
      }
    }

    // For other providers, return default
    if (provider === 'anthropic') return anthropic;
    if (provider === 'google') return google;
    if (provider === 'groq') return groq;

    return anthropic; // fallback
  };

  return (
    <div className="pricing-page">
      <motion.div
        className="container py-5"
        initial="hidden"
        animate="visible"
        variants={animations.container}
      >
        <div className="row min-vh-100 py-5">
          {/* Header */}
          <motion.div className="col-12 mb-4" variants={animations.item}>
            <h1 className="pricing-main-title text-center pt-5 mb-4">
              <span className="title-ai-model">AI Model</span>{' '}
              <span className="title-pricing">Pricing</span>
            </h1>
            <p className="pricing-description text-center pb-5 mb-4">
              Power your ideas with cutting-edge AI models.
            </p>
          </motion.div>

          {/* Subscription Cards */}
          <motion.div
            className="row mt-5 pt-5"
            initial="hidden"
            animate="visible"
            variants={animations.container}
          >
            <motion.div className="col-12 mb-5" variants={animations.item}>
              <h2 className="subscription-title text-center mb-2">
                <span className="title-available">Available</span>{' '}
                <span className="title-subscriptions">Subscriptions</span>
              </h2>
              <p className="subscription-description text-center mb-5">
                From indie creators to full-scale AI operations — get monthly
                credits, storage, and power tailored to your workflow.
              </p>
            </motion.div>
            {subscriptionPlans.map((plan, index) => (
              <motion.div
                key={index}
                className="col-lg-4 col-md-6 col-sm-12 mb-4 mx-auto"
                variants={animations.item}
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.6 }}
              >
                <SubscriptionCard {...plan} />
              </motion.div>
            ))}
          </motion.div>

          {/* Top Ups Section */}
          <motion.div
            className="row mt-5 pt-5"
            initial="hidden"
            animate="visible"
            variants={animations.container}
          >
            <motion.div className="col-12 mb-5" variants={animations.item}>
              <h2 className="subscription-title text-center mb-2">
                <span className="title-available">Top</span>{' '}
                <span className="title-subscriptions">Ups</span>
              </h2>
              <p className="subscription-description text-center mb-5">
                Add extra credits anytime and keep your AI services running
                without interruption. Perfect for those busy months when your
                projects exceed expectations.
              </p>
            </motion.div>

            {/* First row - 3 cards */}
            <div className="row mb-4">
              {topUpPlans.slice(0, 3).map((plan, index) => (
                <motion.div
                  key={index}
                  className="col-lg-4 col-md-6 col-sm-12 mb-4"
                  variants={animations.item}
                  initial={{ opacity: 0, y: 50 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1, duration: 0.6 }}
                >
                  <TopUpCard {...plan} />
                </motion.div>
              ))}
            </div>

            {/* Second row - 2 cards */}
            <div className="row justify-content-center pb-5">
              {topUpPlans.slice(3, 5).map((plan, index) => (
                <motion.div
                  key={index + 3}
                  className="col-lg-4 col-md-6 col-sm-12 mb-4"
                  variants={animations.item}
                  initial={{ opacity: 0, y: 50 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: (index + 3) * 0.1, duration: 0.6 }}
                >
                  <TopUpCard {...plan} />
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Table */}
          <motion.div className="col-12" variants={animations.item}>
            <div className="pricing-card">
              <div className="table-responsive">
                <table className="table mb-0 pricing-table">
                  <thead>
                    <tr>
                      {[
                        { icon: 'bi-building', text: 'Provider' },
                        { icon: 'bi-cpu', text: 'Model' },
                        {
                          icon: 'bi-code-slash',
                          text: 'Model ID',
                          class: 'd-none d-lg-table-cell',
                        },
                        {
                          icon: 'bi-arrow-right',
                          text: 'Prompt',
                          subtext: 'Credits/M',
                        },
                        {
                          icon: 'bi-arrow-left',
                          text: 'Completion',
                          subtext: 'Credits/M',
                        },
                        { icon: 'bi-check-circle', text: 'Status' },
                      ].map((header, i) => (
                        <th
                          key={i}
                          className={`border-0 py-4 px-4 text-center ${
                            header.class || ''
                          }`}
                        >
                          <div className="header-content">
                            <i className={`bi ${header.icon} mb-2`}></i>
                            <div className="header-text">{header.text}</div>
                            {header.subtext && (
                              <div className="header-subtext">
                                {header.subtext}
                              </div>
                            )}
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.map((model, index) => (
                      <motion.tr
                        key={index}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.05, duration: 0.3 }}
                        className="table-row"
                      >
                        <td className="py-3 px-4 border-0 text-center">
                          <div className="provider-cell d-flex align-items-center justify-content-center">
                            <img
                              src={getProviderImage(
                                model.service_provider.name
                              )}
                              alt={model.service_provider.name}
                              className={`provider-image ${
                                model.service_provider.name === 'mindmade'
                                  ? 'mindmade-logo'
                                  : ''
                              }`}
                            />
                            <span className="provider-badge">
                              {model.service_provider.name === 'openai'
                                ? 'OpenAI'
                                : model.service_provider.name === 'anthropic'
                                  ? 'Anthropic'
                                  : model.service_provider.name === 'google'
                                    ? 'Google'
                                    : model.service_provider.name === 'groq'
                                      ? 'Groq'
                                      : model.service_provider.name ===
                                          'mindmade'
                                        ? 'Mindmade'
                                        : model.service_provider.name}
                            </span>
                          </div>
                        </td>
                        <td className="py-3 px-4 border-0 text-center">
                          <span className="model-name">{model.name}</span>
                        </td>
                        <td className="py-3 px-4 border-0 text-center d-none d-lg-table-cell">
                          <code className="model-id">{model.model_id}</code>
                        </td>
                        <td className="py-3 px-4 border-0 text-center">
                          <span className="credit-value">
                            {model.price_per_million_prompt_tokens}
                          </span>
                        </td>
                        <td className="py-3 px-4 border-0 text-center">
                          <span className="credit-value">
                            {model.price_per_million_completion_tokens}
                          </span>
                        </td>
                        <td className="py-3 px-4 border-0 text-center">
                          <span className="status-badge d-inline-flex align-items-center justify-content-center">
                            <i className="bi bi-check-circle-fill me-1"></i>
                            {model.status}
                          </span>
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </motion.div>

          {/* Footer */}
          <motion.div
            className="col-12 text-center mt-5"
            variants={animations.item}
          >
            <div className="row justify-content-center">
              <div className="col-md-8">
                <div className="d-flex flex-wrap justify-content-center gap-4 mb-4">
                  {['Pay per use', 'No hidden fees', 'Real-time billing'].map(
                    (text, i) => (
                      <div
                        key={i}
                        className="d-flex align-items-center text-muted small"
                      >
                        <i className="bi bi-check-circle-fill text-success me-2"></i>
                        {text}
                      </div>
                    )
                  )}
                </div>
                <p className="text-muted small">
                  Credits are consumed based on token usage. 1M tokens ≈ 750,000
                  words. All prices are in credits and subject to change.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
};

export default Pricing;
